<script lang="ts">
    import { onDestroy, tick } from 'svelte'

    import { DateTime } from 'luxon'
    import { filter } from 'rxjs/operators'

    import { gettext as _ } from 'hydria/services/gettext'
    import { simpleFetch, type SimpleFetchResult } from 'hydria/services/session'
    import { sgoto } from 'hydria/svelte_helpers'

    import { stream } from 'wdom/services/stream'
    import type { StreamEvent } from 'wdom/services/stream'
    import { d3, nv, nvReady } from 'wdom/utils/nvd'

    import LoadingPattern from 'hydria/components/LoadingPattern.svelte'

    const UPDATE_TIMEOUT_MS = 310 * 1000

    // After a task "came in" through the stream, we update "sooner"
    const UPDATE_SOON_TIMEOUT_MS = 10 * 1000

    const STATE_TYPES = ['RUNNING', 'READY', 'SUCCESS', 'FAILED']
    const STATE_LABELS = ['Running', 'Ready', 'Success', 'Failed']
    // const STATE_COLORS = ['#9467bd', '#ff7f0e', '#1f77b4', '#d62728']
    const STATE_COLORS = ['#1f77b4', '#ff7f0e', '#9467bd', '#d62728']

    const unsubs: any[] = []
    onDestroy(() => {
        for (const unsub of unsubs) unsub.unsubscribe != null ? unsub.unsubscribe() : unsub()
    })

    let chart: any
    let chartSvg: any

    interface StateCounts {
        RUNNING?: number
        READY?: number
        SUCCESS?: number
        FAILED?: number
    }
    let states: StateCounts = {}

    interface InDailyCount {
        days_ago: number
        values: StateCounts
    }

    interface GData {
        key: string
        color: string
        type: string
        values: {
            x: number
            y: number
        }[]
    }

    let gdata: GData[] = []

    let loading = $state(true)
    let updating = $state(false)
    let error = $state(false)
    let eventTimer: ReturnType<typeof setTimeout> | undefined = undefined

    let innerWidth: number = $state(0)

    async function loadStats() {
        if (!loading) updating = true
        error = false
        let r: SimpleFetchResult

        try {
            r = await simpleFetch('GET', '/json/task/1/stats', { data: JSON.stringify({ days: 7, with_daily: true }, null, 0) })
        } catch (e) {
            console.error('Failed to load task stats:', e)
            error = true
            loading = false
            updating = false
            return
        }

        states = r.data.state_counts

        gdata = []

        for (let i = 0; i < STATE_TYPES.length; i++) {
            gdata.push({
                key: STATE_LABELS[i],
                color: STATE_COLORS[i],
                type: STATE_TYPES[i],
                values: [],
            })
        }

        for (const dstat of r.data.daily_counts as InDailyCount[]) {
            for (const [state, count] of Object.entries(dstat.values)) {
                const pos = STATE_TYPES.indexOf(state)
                if (pos < 0) console.error('Unknown state type:', state)
                else {
                    gdata[pos].values.push({
                        x: dstat.days_ago,
                        y: count,
                    })
                }
            }
        }

        if (!chart) createChart()
        else chartSvg.datum(gdata).call(chart)

        loading = false
        updating = false
        eventTimer = setTimeout(() => {
            eventTimer = undefined
            loadStats()
        }, UPDATE_TIMEOUT_MS)
    }

    function updateSoon() {
        if (eventTimer) {
            try {
                clearTimeout(eventTimer)
            } catch {}
        }
        eventTimer = setTimeout(() => {
            eventTimer = undefined
            loadStats()
        }, UPDATE_SOON_TIMEOUT_MS)
    }

    function createChart() {
        tick().then(() => {
            nv.addGraph(() => {
                chart = nv.models.multiBarChart().id(
                    Math.random()
                        .toString(36)
                        .slice(2, 2 + 15),
                )
                // chart.height(275)
                // chart.width(275)
                chart.xAxis
                    // .rotateLabels(50)
                    .showMaxMin(false)
                    .tickFormat((d: number) => {
                        // return DateTime.now().minus({ days: d }).toFormat('yyyy-MM-dd')
                        return DateTime.now().minus({ days: d }).toFormat('MM-dd')
                    })
                chart.tooltip.enabled(true)
                chart.multibar.stacked(true)
                chart.showControls(false)
                chart.reduceXTicks(false)
                chart.margin({
                    top: 25,
                    right: 20,
                    // bottom: 75,
                    bottom: 20,
                    left: 45,
                })

                chart.multibar.dispatch.on('elementClick', (e) => {
                    const type = e.series.type
                    sgoto(`/tasks?limit_to_days=7&state=${type}`)
                })

                chartSvg = d3.select('#task-stat-graph')
                chartSvg.datum(gdata).call(chart)
            })
        })
    }

    function handleResize(w: number) {
        if (chart && chart.update) chart.update()
    }

    $effect(() => {
        handleResize(innerWidth)
    })

    onDestroy(() => {
        if (eventTimer) {
            try {
                clearTimeout(eventTimer)
            } catch {}
            eventTimer = undefined
        }
        if (chartSvg) {
            if (chart.tooltip && chart.tooltip.id) d3.select(`#${chart.tooltip.id()}`).remove()
            chartSvg.remove()
        }
    })

    if ($nvReady) loadStats()

    unsubs.push(
        stream.pipe(filter((e) => (e as StreamEvent).event == 'task')).subscribe({
            next: (e) => {
                const task = { ...(e as StreamEvent).data }

                if (task.state == 'SUCCESS' || task.state == 'FAILED') {
                    // On fait -1 à RUNNING.. qui est le cas le plus fréquent
                    for (const gd of gdata) {
                        if (gd.type == 'RUNNING') {
                            if (gd.values[gd.values.length - 1].y) gd.values[gd.values.length - 1].y--
                            break
                        }
                    }
                }
                for (const gd of gdata) {
                    if (gd.type == task.state) gd.values[gd.values.length - 1].y++
                }
                gdata = gdata
                if (chartSvg) chartSvg.datum(gdata).call(chart)

                updateSoon()
            },
        }),
    )
</script>

<svelte:window bind:innerWidth />

<div class="card">
    <div class="card-title text-primary">
        <span>{$_('Tasks (7 days)')}</span>
        {#if updating}
            <span><div class="spinner-border spinner-border-sm" role="status" aria-hidden="true"></div></span>
        {/if}
    </div>

    {#if loading}
        <div class="card-body d-flex gap-3">
            <div style="flex: 1 0 0">
                <LoadingPattern pattern={['x']} />
            </div>
            <div>{$_('Loading…')}</div>
            <div style="flex: 1 0 0">
                <LoadingPattern pattern={['x']} />
            </div>
        </div>
    {:else if error}
        <div class="card-body">
            <div class="text-danger">Les tâches n’ont pas pu être chargées.</div>
        </div>
    {:else}
        <div class="card-body">
            <svg id="task-stat-graph" style="height: 275px; width: 100%" class="nvd3-svg" />
        </div>
    {/if}
</div>
